import type {
  ApiResponse,
  DeviceRecord,
  DirectEpochTransitionClaim,
  WSMessageAckData,
  WSMessageSyncResultData,
  WSMessageSyncStatusResultData,
  WSMessageMutationUpdateData,
  WSMessageStatusUpdateData,
  WSReadCursorPushData,
  DirectMessageReplyReference,
  PublicKey,
} from '@shared/types';
import { signedIdentityRequest } from './signedIdentityApi';

const DIRECT_MESSAGES_SCOPE = 'direct message';

export const directMessagesApi = {
  async sendMessage(
    payload: {
      recipientIdentityId: string;
      ciphertext: string;
      senderCiphertext?: string;
      senderSignature: string;
      clientMessageId: string;
      clientCreatedAt?: number;
      epoch?: number;
      replyTo?: DirectMessageReplyReference | null;
    },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ ack: WSMessageAckData; statusUpdate?: WSMessageStatusUpdateData | null }>> {
    return signedIdentityRequest({
      path: '/messages/send',
      type: 'msg:http:send',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async updateStatus(
    payload: { serverMessageId: string; status: 'delivered' | 'read' },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ statusUpdate: WSMessageStatusUpdateData | null }>> {
    return signedIdentityRequest({
      path: '/messages/status',
      type: 'msg:http:status',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async listMessages(
    payload: { since?: number; limit?: number; peerIdentityId?: string },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<WSMessageSyncResultData>> {
    return signedIdentityRequest({
      path: '/messages/list',
      type: 'msg:http:list',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async listStatusUpdates(
    payload: { since?: number; limit?: number; peerIdentityId?: string },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<WSMessageSyncStatusResultData>> {
    return signedIdentityRequest({
      path: '/messages/status-list',
      type: 'msg:http:status-list',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async editMessage(
    payload: { serverMessageId: string; ciphertext: string; senderCiphertext?: string; senderSignature: string; epoch?: number },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ mutation: WSMessageMutationUpdateData }>> {
    return signedIdentityRequest({
      path: '/messages/edit',
      type: 'msg:http:edit',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async deleteMessage(
    payload: { serverMessageId: string },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ mutation: WSMessageMutationUpdateData | null }>> {
    return signedIdentityRequest({
      path: '/messages/delete',
      type: 'msg:http:delete',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async clearThread(
    payload: { peerIdentityId: string },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ deletedCount: number }>> {
    return signedIdentityRequest({
      path: '/messages/clear',
      type: 'msg:http:clear',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async markRead(
    payload: { peerIdentityId: string; readThrough: number },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ statusUpdates: WSMessageStatusUpdateData[]; readCursor: WSReadCursorPushData }>> {
    return signedIdentityRequest({
      path: '/messages/mark-read',
      type: 'msg:http:mark-read',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async claimOrPublishEpochKey(
    payload: {
      peerIdentityId: string;
      epoch: number;
      keyCommitment: string;
      signedEpochTransition: DirectEpochTransitionClaim;
      envelopes: Array<{ identityId: string; envelopeCiphertext: string }>;
    },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ directChatId: string; epoch: number; keyCommitment: string; accepted: boolean; signedEpochTransition?: DirectEpochTransitionClaim | null; updated: number }>> {
    return signedIdentityRequest({
      path: '/messages/key/claim-or-publish',
      type: 'msg:http:key-claim',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },

  async fetchEpochKey(
    payload: { peerIdentityId: string; epoch?: number },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{
    directChatId: string;
    participantIdentityIds: [string, string];
    epoch: number;
    keyCommitment: string | null;
    signedEpochTransition?: DirectEpochTransitionClaim | null;
    proposerIdentityId?: string | null;
    proposerDeviceId?: string | null;
    envelopeCiphertext: string | null;
    publisherIdentityId: string | null;
    identityPublicKeys: Record<string, PublicKey>;
    devicesByIdentityId: Record<string, DeviceRecord[]>;
    hasEnvelope: boolean;
    hasEpochKey?: boolean;
    canRecoverByRekey?: boolean;
  }>> {
    return signedIdentityRequest({
      path: '/messages/key/fetch',
      type: 'msg:http:key-fetch',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: DIRECT_MESSAGES_SCOPE
    });
  },
};
